// src/utils/chunk-error.ts
// Utilitários compartilhados para detecção e recovery de chunk errors

const RELOAD_KEY = 'chunk_reload_attempted';
const RELOAD_TTL = 60_000; // 1 minuto

// Flag em memória: fica true a partir do momento em que ESTE ciclo de página
// agenda um reload, até a navegação de fato trocar o documento. É mais exata
// que `hasReloadedRecently` (que é aproximação por janela de 60s via
// sessionStorage) e independe de storage — vale mesmo em aba anônima/iframe
// onde sessionStorage pode falhar. Os boundaries usam isto pra renderizar
// neutro (spinner) em QUALQUER erro enquanto o reload está a caminho, cobrindo
// inclusive erros derivados que não casam com `isChunkLoadError`.
let reloadInFlight = false;

/**
 * Há um reload já agendado neste ciclo de página?
 */
export function isReloading(): boolean {
  return reloadInFlight;
}

// O documento está saindo pra outro endereço por decisão do próprio app
// (redirect pro portal, logout, sessão expirada). Ao trocar de página o
// navegador aborta os import() em andamento — o Safari do iPhone dispara
// `vite:preloadError` ("Importing a module script failed.") — e isso NÃO é
// chunk obsoleto: recarregar aqui cancelava a ida pro portal e voltava pra
// mesma tela, e o segundo aborto caía no TTL e virava evento no Better Stack
// (13 de 13 casos no iPhone em 14–15/10).
let leavingPage = false;

/** Marca que este documento está indo embora. Chamar ANTES de navegar. */
export function markLeavingPage(): void {
  leavingPage = true;
}

/** Volta do bfcache: o documento é o mesmo e está vivo de novo. */
export function clearLeavingPage(): void {
  leavingPage = false;
}

export function isLeavingPage(): boolean {
  return leavingPage;
}

/** Sai do app pra `url` (navegação completa), marcando a saída antes. */
export function leavePageTo(url: string): void {
  markLeavingPage();
  window.location.href = url;
}

/**
 * Detecta se um erro é relacionado a falha de carregamento de chunk.
 * Cobre: HTML servido como JS (SyntaxError '<'), dynamic import failure,
 * CSS preload failure, e webpack legacy chunk errors.
 */
export function isChunkLoadError(error: Error | string): boolean {
  const msg = typeof error === 'string' ? error : error?.message || '';
  return (
    msg.includes("Unexpected token '<'") ||
    msg.includes('Failed to fetch dynamically imported module') ||
    // O mesmo import() falho, nas palavras do Safari e do Firefox. Sem estes,
    // nesses navegadores o ErrorPage mostrava "erro inesperado" em vez de
    // recarregar.
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Unable to preload CSS') ||
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError')
  );
}

/**
 * Verifica se já houve tentativa de reload recente.
 */
export function hasReloadedRecently(): boolean {
  try {
    const ts = sessionStorage.getItem(RELOAD_KEY);
    if (!ts) return false;
    return Date.now() - Number(ts) < RELOAD_TTL;
  } catch {
    // sessionStorage pode estar indisponível (iframe, modo privado, etc.)
    return false;
  }
}

/**
 * Marca que um reload foi executado.
 */
export function markReload(): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // silently fail
  }
}

/**
 * Limpa a flag de reload — chamar quando o app montar com sucesso.
 */
export function clearReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // silently fail
  }
}

/**
 * Tenta fazer reload uma única vez. Retorna false se já foi tentado recentemente.
 * Opcional: envia evento para tracking antes do reload.
 */
export function tryReloadOnce(context?: string): boolean {
  if (hasReloadedRecently()) return false;

  // Log para monitoramento (Sentry, analytics, etc.)
  console.warn(`[chunk-error] Reload triggered — context: ${context || 'unknown'}`);

  markReload();
  // Só marca "reload em voo" no ramo que de fato recarrega. No caminho
  // bloqueado pelo TTL (early return acima) a flag continua false, senão o
  // usuário nunca veria a tela de "Atualização disponível" com botão.
  reloadInFlight = true;
  window.location.reload();
  return true;
}